import React, { useState, useEffect } from 'react';
import {
    MapPin,
    Plus,
    ChevronLeft,
    MoreVertical,
    Eye,
    Edit,
    Users,
    Package,
    Activity,
    DollarSign,
    Shield,
    Download,
    Trash2,
    Loader2,
    AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent
} from '../../components/ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';
import AddLocationModal from './AddLocationModal';

interface LocationsProps {
    onBack: () => void;
}

const Locations: React.FC<LocationsProps> = ({ onBack }) => {
    const [locations, setLocations] = useState<any[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<any>(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [locData, statsData] = await Promise.all([
                api.get('/api/inventory/locations'),
                api.get('/api/inventory/locations/stats')
            ]);
            setLocations(locData);
            setStats(statsData);
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (location: any) => {
        setSelectedLocation(location);
        setIsAddModalOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedLocation) return;
        try {
            await api.delete(`/api/inventory/locations/${selectedLocation.id}`);
            toast.success('Location deleted successfully');
            fetchData();
            setIsDeleteModalOpen(false);
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete location');
        }
    };

    const summaryCards = [
        { label: 'Total Locations', value: stats?.totalLocations || 0, icon: <MapPin size={20} className="text-blue-500" />, bg: 'bg-blue-50' },
        { label: 'Active Locations', value: stats?.activeLocations || 0, icon: <Activity size={20} className="text-green-500" />, bg: 'bg-green-50' },
        { label: 'Total Items', value: stats?.totalItems || 0, icon: <Package size={20} className="text-orange-500" />, bg: 'bg-orange-50' },
        { label: 'Total Value', value: `$${(stats?.totalValue || 0).toLocaleString()}`, icon: <DollarSign size={20} className="text-purple-500" />, bg: 'bg-purple-50' }
    ];

    const getInitials = (name: string) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    return (
        <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onBack}
                        className="rounded-xl flex items-center gap-1.5 text-gray-400 hover:text-gray-900 transition-all p-0 h-auto hover:bg-transparent"
                    >
                        <ChevronLeft size={20} />
                        <span className="text-sm font-bold sm:inline hidden">Settings</span>
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">Locations</h1>
                        <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest mt-1.5 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                            Site Management
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <Button variant="outline" className="hidden sm:flex flex-initial rounded-2xl h-11 px-6 gap-2 border-gray-100 font-bold shadow-sm text-xs items-center hover:bg-gray-50 transition-all">
                        <Download size={16} />
                        Export
                    </Button>
                    <Button
                        onClick={() => { setSelectedLocation(null); setIsAddModalOpen(true); }}
                        className="flex-1 sm:flex-initial items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl h-11 px-4 sm:px-6 font-bold shadow-lg shadow-slate-900/10 transition-all text-xs sm:text-sm"
                    >
                        <Plus size={18} />
                        <span className="whitespace-nowrap">Add Location</span>
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {summaryCards.map((card, i) => (
                    <div key={i} className="bg-white p-4 sm:p-6 rounded-[24px] border border-gray-100 shadow-sm flex items-center gap-3 sm:gap-4 group hover:shadow-md transition-all duration-300">
                        <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 duration-300 shrink-0", card.bg)}>
                            {React.cloneElement(card.icon as React.ReactElement, { size: 18, className: (card.icon as React.ReactElement).props.className })}
                        </div>
                        <div className="min-w-0">
                            <p className="text-[9px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider truncate">{card.label}</p>
                            <p className="text-lg sm:text-xl font-black text-gray-900 mt-0.5">{card.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Loading State */}
            {loading && locations.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                    <Loader2 size={28} className="animate-spin text-blue-500" />
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Loading Locations...</p>
                </div>
            )}

            {/* Empty State */}
            {!loading && locations.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 space-y-4 text-gray-300">
                    <div className="w-20 h-20 bg-gray-50 rounded-[28px] flex items-center justify-center shadow-inner">
                        <MapPin size={36} strokeWidth={1.5} />
                    </div>
                    <div className="text-center">
                        <p className="text-sm font-black text-gray-400 uppercase tracking-widest">No Locations Found</p>
                        <p className="text-[10px] text-gray-300 font-bold mt-1">Create your first site to get started</p>
                    </div>
                </div>
            )}

            {/* Mobile Card View */}
            {locations.length > 0 && (
                <div className="md:hidden space-y-4">
                    {locations.map((loc) => (
                        <div key={loc.id} className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
                            <div className="p-4 space-y-4">
                                {/* Card Header */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-11 h-11 bg-blue-50 rounded-[14px] flex items-center justify-center text-blue-600 shrink-0">
                                            <MapPin size={20} />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-sm font-black text-gray-900 truncate">{loc.name}</h3>
                                            <p className="text-[10px] text-gray-400 font-bold truncate">{loc.address || 'No address set'}</p>
                                        </div>
                                    </div>
                                    <Badge className={cn(
                                        "rounded-full px-2 py-0.5 text-[8px] font-black uppercase tracking-wider shrink-0 border-0",
                                        loc.isActive ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"
                                    )}>
                                        {loc.isActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                </div>

                                {/* Manager Chip */}
                                <div className="bg-gray-50/80 rounded-[16px] p-3 flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[9px] font-black shadow-sm border border-gray-100 shrink-0">
                                        {loc.manager ? getInitials(loc.manager.name) : 'NM'}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-xs font-bold text-gray-800 leading-none truncate">
                                            {loc.manager ? loc.manager.name : 'No Manager'}
                                        </p>
                                        <p className="text-[8px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">Manager</p>
                                    </div>
                                </div>

                                {/* Stats Row */}
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="bg-gray-50/50 rounded-[14px] p-3 text-center">
                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Items</p>
                                        <p className="text-base font-black text-gray-900 mt-0.5">{loc.totalItems}</p>
                                    </div>
                                    <div className="bg-red-50/50 rounded-[14px] p-3 text-center">
                                        <p className="text-[8px] font-black text-red-400 uppercase tracking-widest">Low Stock</p>
                                        <p className="text-base font-black text-red-600 mt-0.5">{loc.lowStockItemsCount}</p>
                                    </div>
                                    <div className="bg-purple-50/50 rounded-[14px] p-3 text-center">
                                        <p className="text-[8px] font-black text-purple-400 uppercase tracking-widest">Value</p>
                                        <p className="text-base font-black text-gray-900 mt-0.5">${(parseFloat(loc.inventoryValue) || 0).toLocaleString(undefined, { notation: 'compact' } as any)}</p>
                                    </div>
                                </div>

                                {/* Category Breakdown */}
                                {loc.categoryBreakdown && loc.categoryBreakdown.length > 0 && (
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        {loc.categoryBreakdown.map((cat: any, i: number) => (
                                            <div key={i} className="bg-gray-50 rounded-full px-2.5 py-1 flex items-center gap-1.5">
                                                <div className={cn("w-1.5 h-1.5 rounded-full", cat.name === 'Uniform' ? 'bg-blue-400' : 'bg-orange-400')} />
                                                <span className="text-[9px] font-bold text-gray-600">{cat.name}: {cat.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Mobile Action Bar */}
                            <div className="border-t border-gray-50 px-3 py-2 flex items-center justify-between bg-gray-50/30">
                                <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="sm" className="h-8 px-3 rounded-lg text-[10px] font-bold text-gray-500 hover:text-blue-600 hover:bg-blue-50 gap-1.5">
                                        <Eye size={12} />
                                        View
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 px-3 rounded-lg text-[10px] font-bold text-gray-500 hover:text-gray-900 hover:bg-gray-100 gap-1.5"
                                        onClick={() => handleEdit(loc)}
                                    >
                                        <Edit size={12} />
                                        Edit
                                    </Button>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 rounded-lg hover:bg-red-50 hover:text-red-600 text-gray-400 transition-colors"
                                    onClick={() => { setSelectedLocation(loc); setIsDeleteModalOpen(true); }}
                                >
                                    <Trash2 size={13} />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Desktop Grid */}
            {locations.length > 0 && (
                <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-6">
                    {locations.map((loc) => (
                        <div key={loc.id} className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden group hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 flex flex-col h-full">
                            <div className="p-6 sm:p-8 space-y-6 flex-1">
                                {/* Card Header */}
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3 sm:gap-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shrink-0">
                                            <MapPin size={20} className="sm:w-6 sm:h-6" />
                                        </div>
                                        <div className="max-w-[150px] sm:max-w-none">
                                            <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-tight truncate">{loc.name}</h3>
                                            <p className="text-[10px] sm:text-xs text-gray-400 font-medium truncate">{loc.address || 'No address set'}</p>
                                        </div>
                                    </div>
                                    <Badge className={cn("rounded-lg px-2 sm:px-2.5 py-0.5 sm:py-1 text-[8px] sm:text-[10px] font-bold uppercase tracking-wider shrink-0", loc.isActive ? "bg-green-50 text-green-600 border-green-100" : "bg-gray-50 text-gray-400 border-gray-100")}>
                                        {loc.isActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                </div>

                                {/* Manager Info */}
                                <div className="bg-gray-50/50 rounded-2xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4 border border-gray-100/50 hover:bg-gray-50 transition-colors">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white flex items-center justify-center text-[8px] sm:text-[10px] font-black shadow-sm border border-gray-100 shrink-0">
                                        {loc.manager ? getInitials(loc.manager.name) : 'NM'}
                                    </div>
                                    <div>
                                        <p className="text-xs sm:text-sm font-bold text-gray-800 leading-none mb-1">
                                            {loc.manager ? loc.manager.name : 'No Manager'}
                                        </p>
                                        <p className="text-[8px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-wider">Location Manager</p>
                                    </div>
                                </div>

                                {/* Stats */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Total Items</span>
                                        <span className="text-sm font-black text-gray-700">{loc.totalItems}</span>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Low Stock</span>
                                        <Badge variant="destructive" className="bg-red-50 text-red-500 border-red-100 rounded-lg px-2 h-5 text-[10px]">
                                            {loc.lowStockItemsCount}
                                        </Badge>
                                    </div>
                                    <div className="col-span-2 space-y-1 pt-1 border-t border-gray-100">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Inventory Value</span>
                                        <span className="text-lg font-black text-gray-900">${(parseFloat(loc.inventoryValue) || 0).toLocaleString()}</span>
                                    </div>
                                </div>

                                {/* Category Breakdown */}
                                <div className="space-y-3 pt-2">
                                    <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Category Breakdown</p>
                                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                                        {loc.categoryBreakdown?.map((cat: any, i: number) => (
                                            <div key={i} className="flex items-center gap-2">
                                                <div className={cn("w-1.5 h-1.5 rounded-full", cat.name === 'Uniform' ? 'bg-blue-400' : 'bg-orange-400')} />
                                                <span className="text-[10px] sm:text-[11px] font-bold text-gray-600">{cat.name}: {cat.count}</span>
                                            </div>
                                        ))}
                                        {(!loc.categoryBreakdown || loc.categoryBreakdown.length === 0) && (
                                            <p className="text-[10px] sm:text-[11px] font-medium text-gray-400 italic">No items linked</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="p-6 sm:p-8 pt-0 flex gap-2">
                                <Button variant="outline" className="flex-1 rounded-2xl h-10 sm:h-11 text-[10px] sm:text-xs font-bold gap-1 sm:gap-2 border-gray-100 shadow-sm transition-all hover:bg-gray-50">
                                    <Eye size={14} />
                                    View
                                </Button>
                                <Button
                                    variant="outline"
                                    className="flex-1 rounded-2xl h-10 sm:h-11 text-[10px] sm:text-xs font-bold gap-1 sm:gap-2 border-gray-100 shadow-sm hover:border-blue-100 hover:text-blue-600 transition-all hover:bg-blue-50/50"
                                    onClick={() => handleEdit(loc)}
                                >
                                    <Edit size={14} />
                                    Edit
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-10 sm:w-11 rounded-2xl h-10 sm:h-11 text-xs font-bold border-gray-100 shadow-sm hover:border-red-100 hover:text-red-600 transition-all hover:bg-red-50/50"
                                    onClick={() => { setSelectedLocation(loc); setIsDeleteModalOpen(true); }}
                                >
                                    <Trash2 size={14} />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Access Permissions Section */}
            <div className="pt-4 sm:pt-8 space-y-4 sm:space-y-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
                        <Shield className="text-gray-400" size={20} />
                    </div>
                    <div>
                        <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">Access Permissions</h2>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5 hidden sm:block">Role-based location access</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
                    <div className="bg-white p-5 sm:p-8 rounded-[24px] sm:rounded-[32px] border border-gray-100 shadow-sm space-y-3 sm:space-y-4">
                        <div className="flex items-center gap-3 text-green-600">
                            <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center sm:bg-transparent sm:w-auto sm:h-auto">
                                <Shield size={18} />
                            </div>
                            <h3 className="font-black text-sm sm:text-base tracking-tight">Admin Access</h3>
                        </div>
                        <p className="text-[11px] sm:text-xs text-gray-500 font-medium leading-relaxed">Full access to all locations and inventory management functions across the entire system.</p>
                        <Badge variant="outline" className="rounded-full sm:rounded-lg bg-green-50 text-green-600 border-green-100 text-[9px] sm:text-[10px] font-bold px-3 py-1 uppercase tracking-wider">Global Permissions</Badge>
                    </div>

                    <div className="bg-white p-5 sm:p-8 rounded-[24px] sm:rounded-[32px] border border-gray-100 shadow-sm space-y-3 sm:space-y-4">
                        <div className="flex items-center gap-3 text-blue-600">
                            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center sm:bg-transparent sm:w-auto sm:h-auto">
                                <Users size={18} />
                            </div>
                            <h3 className="font-black text-sm sm:text-base tracking-tight">Location Manager</h3>
                        </div>
                        <p className="text-[11px] sm:text-xs text-gray-500 font-medium leading-relaxed">Manage inventory, issuances, and reporting for assigned locations only.</p>
                        <Badge variant="outline" className="rounded-full sm:rounded-lg bg-blue-50 text-blue-600 border-blue-100 text-[9px] sm:text-[10px] font-bold px-3 py-1 uppercase tracking-wider">Location Specific</Badge>
                    </div>

                    <div className="bg-white p-5 sm:p-8 rounded-[24px] sm:rounded-[32px] border border-gray-100 shadow-sm space-y-3 sm:space-y-4">
                        <div className="flex items-center gap-3 text-orange-600">
                            <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center sm:bg-transparent sm:w-auto sm:h-auto">
                                <Activity size={18} />
                            </div>
                            <h3 className="font-black text-sm sm:text-base tracking-tight">Field Worker</h3>
                        </div>
                        <p className="text-[11px] sm:text-xs text-gray-500 font-medium leading-relaxed">View-only access to assigned location inventory and personal issuance records.</p>
                        <Badge variant="outline" className="rounded-full sm:rounded-lg bg-orange-50 text-orange-600 border-orange-100 text-[9px] sm:text-[10px] font-bold px-3 py-1 uppercase tracking-wider">Read Only</Badge>
                    </div>
                </div>
            </div>

            <AddLocationModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onRefresh={fetchData}
                location={selectedLocation}
            />

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Location"
                message={`Are you sure you want to delete ${selectedLocation?.name}? This action cannot be undone and may affect inventory records associated with this site.`}
            />
        </div>
    );
};

// --- Delete Confirm Modal ---
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, message }: any) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-sm rounded-[32px] p-6 sm:p-8 overflow-hidden border-none shadow-2xl animate-in fade-in zoom-in-95">
                <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center text-red-500 mb-2">
                        <AlertTriangle size={42} strokeWidth={1.5} />
                    </div>
                    <div>
                        <h3 className="text-xl font-black text-gray-900">{title}</h3>
                        <p className="text-sm text-gray-500 mt-2 font-bold leading-relaxed px-2 tracking-tight opacity-70">{message}</p>
                    </div>
                </div>
                <div className="flex gap-3 mt-8">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="flex-1 rounded-2xl h-14 font-black text-[11px] uppercase tracking-wider border-gray-100 hover:bg-gray-50 transition-all"
                    >
                        Keep Location
                    </Button>
                    <Button
                        onClick={onConfirm}
                        className="flex-1 rounded-2xl h-14 bg-red-600 hover:bg-red-700 text-white font-black text-[11px] uppercase tracking-wider shadow-lg shadow-red-600/20 transition-all active:scale-95"
                    >
                        Confirm Delete
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default Locations;
